import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { CheckCircle2, AlertCircle } from 'lucide-react';
import { cx } from './cx.ts';

interface ToastItem {
  id: number;
  message: ReactNode;
  tone: 'ok' | 'error' | 'plain';
  action?: { label: string; onClick: () => void };
  duration: number;
}

const ToastContext = createContext<{ show: (message: ReactNode, opts?: Partial<Omit<ToastItem, 'id' | 'message'>>) => void } | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const next = useRef(1);
  const show = useCallback((message: ReactNode, opts: Partial<Omit<ToastItem, 'id' | 'message'>> = {}) => {
    const id = next.current++;
    setItems((cur) => [...cur.slice(-2), { id, message, tone: opts.tone ?? 'ok', action: opts.action, duration: opts.duration ?? (opts.action ? 5000 : 2800) }]);
    if (navigator.vibrate && opts.tone !== 'error') {
      try { navigator.vibrate(12); } catch { /* not supported */ }
    }
  }, []);
  const dismiss = useCallback((id: number) => setItems((cur) => cur.filter((t) => t.id !== id)), []);
  return (
    <ToastContext.Provider value={{ show }}>
      {children}
      <div aria-live="polite" className="pointer-events-none fixed inset-x-0 bottom-[calc(84px+env(safe-area-inset-bottom))] z-[60] flex flex-col items-center gap-2 px-4 lg:bottom-6">
        {items.map((t) => <Toast key={t.id} item={t} onDone={() => dismiss(t.id)} />)}
      </div>
    </ToastContext.Provider>
  );
}

function Toast({ item, onDone }: { item: ToastItem; onDone: () => void }) {
  useEffect(() => {
    const h = setTimeout(onDone, item.duration);
    return () => clearTimeout(h);
  }, [item.duration, onDone]);
  return (
    <div
      role={item.tone === 'error' ? 'alert' : 'status'}
      className={cx(
        'animate-rise pointer-events-auto flex w-full max-w-[440px] items-center gap-3 rounded-2xl px-4 py-3 shadow-float',
        'bg-ink text-bg',
      )}
    >
      {item.tone === 'ok' && <CheckCircle2 className="size-5 shrink-0 text-in" />}
      {item.tone === 'error' && <AlertCircle className="size-5 shrink-0 text-out" />}
      <span className="flex-1 text-[14.5px] font-medium leading-snug">{item.message}</span>
      {item.action && (
        <button
          type="button"
          onClick={() => { item.action!.onClick(); onDone(); }}
          className="press -my-1 rounded-lg px-2.5 py-1.5 text-[14px] font-bold text-accent"
        >
          {item.action.label}
        </button>
      )}
    </div>
  );
}

export function useToast() {
  const v = useContext(ToastContext);
  if (!v) throw new Error('useToast outside ToastProvider');
  return v.show;
}
